import React, { createContext, useContext, useState, useEffect } from 'react';
import { authApi, locationsApi } from '../services/api';
import { secureStorage } from '../utils/secureStorage';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [locations, setLocations] = useState([]);

  // Helper to check if user has accounting_admin role
  const isAccountingAdmin = (userData) => {
    if (!userData) return false;
    const roles = userData.roles || [];
    return userData.role === 'accounting_admin' || roles.includes('accounting_admin');
  };

  // Helper to check if user is admin (any admin type)
  const isAdmin = (userData) => {
    if (!userData) return false;
    const roles = userData.roles || [];
    return userData.role === 'admin' || userData.role === 'accounting_admin' || 
           roles.includes('admin') || roles.includes('accounting_admin');
  };

  // Helper to check if user is manager type
  const isManager = (userData) => {
    if (!userData) return false;
    const roles = userData.roles || [];
    return userData.role === 'manager' || userData.role === 'assistant_manager' ||
           roles.includes('manager') || roles.includes('assistant_manager');
  };

  // Get user's accessible locations
  const getUserAccessibleLocations = (userData, allLocations) => {
    if (!userData) return [];
    
    // Admins can access all locations
    if (isAdmin(userData)) {
      return allLocations;
    }
    
    // Managers can access their managed locations
    if (isManager(userData)) {
      // Support both snake_case and camelCase
      const managedLocations = userData.managed_locations || userData.managedLocations || [];
      // Fall back to single managed_location if array is empty
      if (managedLocations.length === 0 && (userData.managed_location || userData.managedLocation)) {
        const singleLoc = userData.managed_location || userData.managedLocation;
        return allLocations.filter(l => l.id === singleLoc);
      }
      return allLocations.filter(l => managedLocations.includes(l.id));
    }
    
    // Employees can only see their own location
    const locId = userData.location_id || userData.locationId;
    if (locId) {
      return allLocations.filter(l => l.id === locId);
    }
    
    return [];
  };

  useEffect(() => {
    // Check for saved session
    const token = secureStorage.getItem('token');
    const savedLocation = secureStorage.getItem('homebase_location');
    
    if (token) {
      // Verify token and get user data
      authApi.getMe()
        .then((userData) => {
          setUser(userData);
          // Load locations first, then set selected location
          locationsApi.getAll().then((locs) => {
            setLocations(locs);
            
            // Validate saved location against user's accessible locations
            const accessibleLocs = getUserAccessibleLocations(userData, locs);
            const accessibleIds = accessibleLocs.map(l => l.id);
            
            if (savedLocation && (savedLocation === 'all' && isAdmin(userData)) || accessibleIds.includes(savedLocation)) {
              setSelectedLocation(savedLocation);
            } else {
              // Set default location based on role
              if (isAdmin(userData)) {
                setSelectedLocation('all');
                secureStorage.setItem('homebase_location', 'all');
              } else if (accessibleLocs.length > 0) {
                setSelectedLocation(accessibleLocs[0].id);
                secureStorage.setItem('homebase_location', accessibleLocs[0].id);
              }
            }
          });
        })
        .catch(() => {
          // Token invalid, clear storage
          localStorage.removeItem('token');
          localStorage.removeItem('homebase_location');
          secureStorage.removeItem('token');
          secureStorage.removeItem('homebase_location');
        })
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, []);

  const loadLocations = async () => {
    try {
      const locs = await locationsApi.getAll();
      setLocations(locs);
    } catch (error) {
      console.error('Failed to load locations:', error);
    }
  };

  const login = async (email, password) => {
    try {
      const response = await authApi.login(email, password);
      
      // Store token
      secureStorage.setItem('token', response.access_token);
      
      // Convert snake_case to camelCase for frontend compatibility
      const userData = {
        ...response.user,
        locationId: response.user.location_id,
        managedLocation: response.user.managed_location,
        managedLocations: response.user.managed_locations || [],
        employeeNumber: response.user.employee_number,
      };
      
      setUser(userData);
      
      // Load locations first
      const locs = await locationsApi.getAll();
      setLocations(locs);
      
      // Set default location based on role
      if (isAdmin(userData)) {
        setSelectedLocation('all');
        secureStorage.setItem('homebase_location', 'all');
      } else if (isManager(userData)) {
        // For managers, set to first managed location
        const managedLocs = userData.managedLocations || [];
        if (managedLocs.length > 0) {
          setSelectedLocation(managedLocs[0]);
          secureStorage.setItem('homebase_location', managedLocs[0]);
        } else if (userData.managedLocation || userData.managed_location) {
          const loc = userData.managedLocation || userData.managed_location;
          setSelectedLocation(loc);
          secureStorage.setItem('homebase_location', loc);
        }
      } else {
        setSelectedLocation(userData.locationId || userData.location_id);
        secureStorage.setItem('homebase_location', userData.locationId || userData.location_id);
      }
      
      return { success: true };
    } catch (error) {
      return { success: false, error: error.message || 'Invalid email or password' };
    }
  };

  const logout = () => {
    setUser(null);
    setSelectedLocation(null);
    setLocations([]);
    secureStorage.removeItem('token');
    secureStorage.removeItem('homebase_location');
    // Clean up any legacy raw localStorage keys
    localStorage.removeItem('token');
    localStorage.removeItem('homebase_location');
  };

  const changeLocation = (locationId) => {
    setSelectedLocation(locationId);
    secureStorage.setItem('homebase_location', locationId);
  };

  const hasAccessToLocation = (locationId) => {
    if (!user) return false;
    if (isAdmin(user)) return true;
    
    // Check managed_locations array for managers
    if (isManager(user)) {
      const managedLocs = user.managed_locations || user.managedLocations || [];
      if (managedLocs.length > 0) {
        return managedLocs.includes(locationId);
      }
      // Fall back to single managed_location
      return user.managedLocation === locationId || user.managed_location === locationId;
    }
    
    return user.locationId === locationId || user.location_id === locationId;
  };

  // Get locations that current user can access (for dropdown)
  const getAccessibleLocations = () => {
    return getUserAccessibleLocations(user, locations);
  };

  // Check if user can see "All Locations" option
  const canSeeAllLocations = () => {
    return isAdmin(user);
  };

  // Auto sign-out after 30 minutes of inactivity for managers/assistant managers
  useEffect(() => {
    if (!user) return;
    const roles = user.roles || [user.role];
    const shouldAutoLogout = roles.some(r => ['manager', 'assistant_manager'].includes(r)) && !roles.some(r => ['admin', 'accounting_admin'].includes(r));
    if (!shouldAutoLogout) return;

    const TIMEOUT = 30 * 60 * 1000;
    const WARNING = 2 * 60 * 1000;
    let logoutTimer = null;
    let warningTimer = null;

    const resetTimers = () => {
      if (logoutTimer) clearTimeout(logoutTimer);
      if (warningTimer) clearTimeout(warningTimer);
      warningTimer = setTimeout(() => {
        alert('You will be signed out in 2 minutes due to inactivity.');
      }, TIMEOUT - WARNING);
      logoutTimer = setTimeout(() => {
        logout();
      }, TIMEOUT);
    };

    const events = ['mousedown', 'keydown', 'scroll', 'touchstart'];
    events.forEach(e => window.addEventListener(e, resetTimers));
    resetTimers();

    return () => {
      events.forEach(e => window.removeEventListener(e, resetTimers));
      if (logoutTimer) clearTimeout(logoutTimer);
      if (warningTimer) clearTimeout(warningTimer);
    };
  }, [user]);

  const value = {
    user,
    loading,
    selectedLocation,
    locations,
    login,
    logout,
    changeLocation,
    hasAccessToLocation,
    loadLocations,
    isAccountingAdmin: () => isAccountingAdmin(user),
    isAdmin: () => isAdmin(user),
    isManager: () => isManager(user),
    getAccessibleLocations,
    canSeeAllLocations,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
